import { useStripePaymentsEnabled } from "../hooks/useStripePaymentsEnabled";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { useDialog } from "../hooks/useDialog";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import React, { useEffect, useState } from "react";
import { getAuthenticatedBackendClient } from "../api/backend.api";
import { createNote } from "../api/backoffice.api";
import { type RefundInstruction, type RefundInstructionList, type RefundTypeEnum } from "../generated/backendClient";
import { formatCents, Money } from "../misc/Money";
import { LinearProgress, TextInput } from "react-admin";
import { useMutation, useQueryClient } from "react-query";
import { email as emailValidator } from "../utils/validators";
import { FormProvider, useForm } from "react-hook-form";

type RefundType = Exclude<RefundTypeEnum, "ANY">;

export function buildRefundNoteBody(refundType: RefundType, refundData: RefundInstructionList): string {
  const refundAmountInCents = refundData.instructions.reduce(
    (total, instruction) => total + Math.round(parseFloat(instruction.amount) * 100),
    0,
  );
  const refundAmount = formatCents(refundAmountInCents);

  if (refundType === "STRIPE_REFUND") {
    return `Guthaben in Höhe von ${refundAmount} über Stripe erstattet.`;
  }

  const manualRefundNote = `Manuelle Rückerstattung von Guthaben in Höhe von ${refundAmount} veranlasst.`;
  if (!refundData.email) {
    return manualRefundNote;
  }

  const { driving_school_email_address, driving_school_email_subject, driving_school_email_text } = refundData.email;
  return `${manualRefundNote}

E-Mail an: ${driving_school_email_address ?? ""}
Betreff: ${driving_school_email_subject ?? ""}

${driving_school_email_text ?? ""}`;
}

export function RefundMultipleDialogButton(props: Parameters<typeof EnabledRefundMultipleDialogButton>[0]) {
  const enabled = useStripePaymentsEnabled();
  return props.refundType === "STRIPE_REFUND" && !enabled ? null : <EnabledRefundMultipleDialogButton {...props} />;
}

function EnabledRefundMultipleDialogButton(props: {
  studentId: string;
  refundType: RefundType;
  style?: React.CSSProperties;
}) {
  const { studentId, refundType, style } = props;
  const queryClient = useQueryClient();
  const { dialogProps, openDialog, closeDialog } = useDialog();
  const [isLoading, setIsLoading] = useState(false);
  const validateEmail = emailValidator();
  const formProps = useForm<RefundInstructionList>({
    defaultValues: { instructions: [], email: null },
  });

  const mutateExecuteRefundInstructions = useMutation(
    async (data: RefundInstructionList) => {
      setIsLoading(true);
      try {
        const backendClient = await getAuthenticatedBackendClient();
        await backendClient.user.userExecuteRefundPrepaidCreditsCreate({
          id: studentId,
          requestBody: data,
        });
      } finally {
        setIsLoading(false);
      }
    },
    {
      onSuccess: async (_response, refundData) => {
        try {
          await createNote({
            studentUid: studentId,
            body: buildRefundNoteBody(refundType, refundData),
          });
        } catch (error) {
          handleError("Die Notiz zur Rückerstattung konnte nicht erstellt werden.", error);
        }
        void queryClient.invalidateQueries(["backendUser"]); // refresh backend user data
        // hack: ensure that the user data is refreshed after some time, as it might take some time until the firestore
        // changes are written and then picked up by react-admin
        window.setTimeout(
          () => void queryClient.invalidateQueries(["students"]), // refresh firebase user data
          5000,
        );
        closeDialog();
        await loadRefundableInvoices();
        showSuccessToast("Die Rückerstattung wurde erfolgreich ausgeführt.");
      },
    },
  );

  const loadRefundableInvoices = async () => {
    setIsLoading(true);
    try {
      const backendClient = await getAuthenticatedBackendClient();
      const response: RefundInstructionList = await backendClient.user.userPrepareRefundPrepaidCreditsCreate({
        id: studentId,
        requestBody: { refund_all: true, refund_type: refundType },
      });
      formProps.setValue("instructions", response.instructions);
      formProps.setValue("email", response.email || null);
    } catch (error) {
      console.error("Failed to load refundable invoices", error);
      showErrorToast(`Die Rückerstattung konnte nicht geladen werden.`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void (async () => {
      await loadRefundableInvoices();
    })();
  }, [studentId, refundType]);

  const refundLocation = refundType === "STRIPE_REFUND" ? "auf Stripe" : "per Fahrschulüberweisung";
  const refundName = refundType === "STRIPE_REFUND" ? "auf Stripe" : "manuell";
  const refundInstructions = formProps.getValues("instructions");
  const refundableAmount = refundInstructions
    ? refundInstructions.reduce((acc, instruction) => acc + parseFloat(instruction.amount), 0)
    : 0;
  const refundPossible = !isLoading && refundableAmount > 0;
  const icon = refundType === "STRIPE_REFUND" ? <RequestQuoteIcon /> : <AccountBalanceIcon />;

  const actionName = (action: RefundInstruction["action"]): string => {
    switch (action) {
      case "refund":
        return "Vollständig auf Stripe erstatten";
      case "refund_partially":
        return "Teilweise auf Stripe erstatten";
      case "refund_manual":
        return "Vollständig per Fahrschulüberweisung erstatten";
      case "refund_partially_manual":
        return "Teilweise per Fahrschulüberweisung erstatten";
    }
  };

  const onSubmit = (data: any) => {
    mutateExecuteRefundInstructions.mutate(data);
  };

  return (
    <div style={style}>
      <>
        <Typography gutterBottom style={{ fontWeight: "bold" }}>
          {refundType === "STRIPE_REFUND" ? "Direkte Rückerstattung" : "Rückerstattung via Überweisung"}
        </Typography>
        <Typography variant="subtitle1">
          {isLoading ? (
            <>Lade...</>
          ) : (
            <>
              <Money cents={refundableAmount * 100} /> können {refundLocation} erstattet werden.
            </>
          )}
        </Typography>
        <Button
          startIcon={icon}
          variant="outlined"
          onClick={openDialog}
          disabled={isLoading || !refundPossible}
          style={{ marginTop: "10px" }}
        >
          {isLoading ? <LinearProgress timeout={0} /> : <>Jetzt {refundName} rückerstatten...</>}
        </Button>
        {refundPossible && (
          <>
            {refundType === "MANUAL_REFUND" && (
              <Alert variant="outlined" severity="info" sx={{ mt: 2, mb: 1 }}>
                Zahlungen, welche über 180 Tage alt sind, können nicht direkt zurückerstattet werden. In diesem Fall
                wird das Guthaben der Fahrschule überwiesen. Diese muss dann das Guthaben von ihrem Konto an den Kunden
                weiter überweisen.
              </Alert>
            )}
            <Dialog {...dialogProps} fullWidth={true} maxWidth="md">
              <DialogTitle>Prepaid-Guthaben zurückerstatten</DialogTitle>
              <DialogCloseButton onClick={closeDialog} />
              <DialogContent sx={{ paddingTop: "20px !important" }}>
                <FormProvider {...formProps}>
                  <Box>
                    <Typography variant="body1">Folgende Quittungen werden erstattet:</Typography>
                    <TableContainer component={Paper}>
                      <Table sx={{ minWidth: 650 }} size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell align="right">Rechnung</TableCell>
                            <TableCell align="right">Geplante Aktion</TableCell>
                            <TableCell align="right">Betrag</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {refundInstructions.map((instruction) => (
                            <TableRow
                              key={instruction.invoice_number}
                              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                            >
                              <TableCell align="right">{instruction.invoice_number}</TableCell>
                              <TableCell align="right">{actionName(instruction.action)}</TableCell>
                              <TableCell align="right">
                                <Money cents={parseFloat(instruction.amount) * 100} />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                        <TableHead>
                          <TableRow>
                            <TableCell align="right" colSpan={3}>
                              Gesamt:{" "}
                              <Money
                                cents={
                                  100 *
                                  refundInstructions.reduce(
                                    (acc, instruction) => acc + parseFloat(instruction.amount),
                                    0,
                                  )
                                }
                              />
                            </TableCell>
                          </TableRow>
                        </TableHead>
                      </Table>
                    </TableContainer>
                    {refundType === "MANUAL_REFUND" && (
                      <Box marginTop="20px">
                        <Stack gap="5px">
                          <TextInput
                            source="email.driving_school_email_address"
                            label="E-Mail Adresse"
                            validate={validateEmail}
                          />
                          <TextInput
                            source="email.driving_school_email_subject"
                            label="Betreff"
                            sx={{ mt: "5px" /* <-- prevents that the input label is truncated */ }}
                          />
                          <TextInput source="email.driving_school_email_text" label="Text" multiline />
                        </Stack>
                      </Box>
                    )}
                    <Button
                      startIcon={icon}
                      onClick={formProps.handleSubmit(onSubmit)}
                      variant="outlined"
                      style={{ marginTop: "20px" }}
                    >
                      Jetzt Prepaid-Guthaben {refundLocation} zurückerstatten
                    </Button>
                  </Box>
                </FormProvider>
              </DialogContent>
            </Dialog>
          </>
        )}
      </>
    </div>
  );
}
